'use client';

import { useState, useEffect, useRef, useCallback } from 'react';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseAutoSaveProps<T> {
  data: T;
  onSave: (data: T, id?: string | null) => Promise<string>;
  userId: string | undefined | null;
  initialEssayId?: string | null;
  debounceMs?: number;
}

interface UseAutoSaveReturn {
  saveStatus: SaveStatus;
  currentEssayId: string | null;
  triggerManualSave: () => Promise<void>;
}

export function useAutoSave<T>({
  data,
  onSave,
  userId,
  initialEssayId = null,
  debounceMs = 2000,
}: UseAutoSaveProps<T>): UseAutoSaveReturn {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [currentEssayId, setCurrentEssayId] = useState<string | null>(
    initialEssayId
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasPendingSaveRef = useRef<boolean>(false);
  const prevDataRef = useRef<T>(data);

  useEffect(() => {
    if (isSaving) return;

    if (JSON.stringify(data) !== JSON.stringify(prevDataRef.current)) {
      hasPendingSaveRef.current = true;
      prevDataRef.current = data;
      setSaveStatus('idle');
    }
  }, [data, isSaving]);

  useEffect(() => {
    if (!userId || !hasPendingSaveRef.current || isSaving) {
      return;
    }

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(async () => {
      if (!userId || !hasPendingSaveRef.current || isSaving) return;

      setIsSaving(true);
      setSaveStatus('saving');
      hasPendingSaveRef.current = false;

      try {
        const savedId = await onSave(data, currentEssayId);
        if (savedId !== currentEssayId) {
          setCurrentEssayId(savedId);
        }
        setSaveStatus('saved');
        console.log('Auto-save successful. Essay ID:', savedId);
      } catch (error) {
        console.error('Auto-save failed:', error);
        setSaveStatus('error');
        hasPendingSaveRef.current = true;
      } finally {
        setIsSaving(false);
      }
    }, debounceMs);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [data, onSave, userId, currentEssayId, debounceMs, isSaving]);

  const triggerManualSave = useCallback(async () => {
    if (!userId || isSaving) {
      console.log('Manual save skipped: No user or already saving.');
      return;
    }

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    console.log('Manually saving data...');
    setIsSaving(true);
    setSaveStatus('saving');
    hasPendingSaveRef.current = false;

    try {
      const savedId = await onSave(data, currentEssayId);
      if (savedId !== currentEssayId) {
        setCurrentEssayId(savedId);
      }
      setSaveStatus('saved');
      console.log('Manual save successful. Essay ID:', savedId);
    } catch (error) {
      console.error('Manual save failed:', error);
      setSaveStatus('error');
      hasPendingSaveRef.current = true;
    } finally {
      setIsSaving(false);
    }
  }, [userId, data, onSave, currentEssayId, isSaving]);

  return { saveStatus, currentEssayId, triggerManualSave };
}
